import React from "react";
import { X } from "lucide-react";
import { useI18n } from "../i18n";

/**
 * LegalModal — bilingual KVKK & ETK "aydınlatma metni" viewer.
 * Props:
 *   - open: boolean
 *   - kind: "kvkk" | "etk"
 *   - onClose: () => void
 */
export default function LegalModal({ open, kind, onClose }) {
  const { lang } = useI18n();
  if (!open) return null;

  const content = TEXT[kind] || TEXT.kvkk;
  const body = lang === "en" ? content.en : content.tr;
  const title = lang === "en" ? content.title_en : content.title_tr;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      data-testid={`legal-modal-${kind}`}
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#eef0e7] px-6 py-4">
          <div className="font-display text-lg text-[#14512f]" data-testid="legal-modal-title">
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-[#6b7268] hover:bg-[#f5f6f1] hover:text-[#14512f]"
            data-testid="legal-modal-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5 text-[13.5px] leading-relaxed text-[#3b423a]">
          {body.map((para, i) => {
            if (para.h) {
              return (
                <h3 key={i} className="mt-4 mb-2 font-display text-[15px] font-semibold text-[#14512f]">
                  {para.h}
                </h3>
              );
            }
            return (
              <p key={i} className="mb-3">
                {para.p}
              </p>
            );
          })}
        </div>
        <div className="border-t border-[#eef0e7] px-6 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-[#14512f] px-4 py-2 text-sm font-medium text-white hover:bg-[#0f4526]"
            data-testid="legal-modal-ok"
          >
            {lang === "en" ? "Close" : "Kapat"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TEXT = {
  kvkk: {
    title_tr: "KVKK Aydınlatma Metni",
    title_en: "KVKK Privacy Notice",
    tr: [
      { h: "Veri Sorumlusu" },
      {
        p:
          "6698 sayılı Kişisel Verilerin Korunması Kanunu (\"KVKK\") uyarınca veri sorumlusu Enasuspro Sürdürülebilirlik Koçluğu (\"Enasuspro\", \"Şirket\") olarak, bu metin ile web sitemiz ve Carbon Ledger uygulamamız üzerinden ilettiğiniz kişisel verilerin işlenmesine ilişkin sizleri bilgilendirmek isteriz.",
      },
      { h: "İşlenen Kişisel Veriler" },
      {
        p:
          "Ad-soyad, e-posta adresi, telefon numarası, şirket adı, ülke ve iletiniz kapsamında paylaştığınız diğer bilgiler.",
      },
      { h: "İşleme Amaçları" },
      {
        p:
          "Talebinizin karşılanması, ürün demosu ve örnek raporun iletilmesi, ticari teklif sunulması, sözleşme öncesi müzakerelerin yürütülmesi ve müşteri ilişkileri yönetimi.",
      },
      { h: "Aktarım" },
      {
        p:
          "Verileriniz yalnızca hizmetin yürütülmesi için işbirliği yaptığımız yurt içi/yurt dışı bulut, e-posta ve altyapı sağlayıcıları ile paylaşılabilir; üçüncü taraflara satış amaçlı aktarılmaz.",
      },
      { h: "Haklarınız" },
      {
        p:
          "KVKK 11. madde kapsamında; kişisel verilerinizin işlenip işlenmediğini öğrenme, düzeltilmesini/silinmesini isteme, işlemeye itiraz etme haklarına sahipsiniz. Taleplerinizi info@enasuspro.com adresine iletebilirsiniz.",
      },
      { h: "Onay" },
      {
        p:
          "Bu kutuyu işaretleyerek yukarıdaki aydınlatma metnini okuduğunuzu ve iletişim bilgilerinizin talep ettiğiniz hizmet kapsamında işlenmesine onay verdiğinizi kabul etmiş sayılırsınız.",
      },
    ],
    en: [
      { h: "Data Controller" },
      {
        p:
          "Under Turkish Law No. 6698 on the Protection of Personal Data (\"KVKK\"), the data controller is Enasuspro Sustainability Coaching (\"Enasuspro\", \"the Company\"). This notice informs you how we process the personal data you submit through our website and the Carbon Ledger application.",
      },
      { h: "Categories of Data" },
      {
        p:
          "Full name, email address, phone number, company name, country and any additional information you share in your message.",
      },
      { h: "Purposes of Processing" },
      {
        p:
          "Handling your request, delivering a product demo and a sample report, preparing commercial offers, running pre-contractual negotiations and managing customer relationships.",
      },
      { h: "Transfers" },
      {
        p:
          "Your data may be shared with the domestic and international cloud, email and infrastructure providers we cooperate with solely to run the service; it is not sold to third parties.",
      },
      { h: "Your Rights" },
      {
        p:
          "Under Article 11 of KVKK you have the right to learn whether your personal data is processed, request its correction or deletion, and object to processing. Requests can be sent to info@enasuspro.com.",
      },
      { h: "Consent" },
      {
        p:
          "By ticking this box you confirm that you have read this privacy notice and consent to the processing of your contact information for the service you have requested.",
      },
    ],
  },
  etk: {
    title_tr: "ETK (Ticari Elektronik İleti) Aydınlatma Metni",
    title_en: "ETK (Commercial Electronic Communication) Notice",
    tr: [
      { h: "Amaç" },
      {
        p:
          "6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun (\"ETK\") ve ilgili Ticari İletişim Yönetmeliği uyarınca, tarafınıza ürün tanıtımı, kampanya, yeni özellik duyurusu ve sürdürülebilirlik içerikleri gibi ticari elektronik iletiler gönderilebilmesi için açık rızanız aranmaktadır.",
      },
      { h: "Kapsam" },
      {
        p:
          "Onay vermeniz halinde e-posta ve/veya SMS aracılığıyla düzensiz aralıklarla iletişim yapılabilir. Bu onay pazarlama amaçlıdır; hizmet kullanımınız için zorunlu değildir.",
      },
      { h: "Onayın Geri Alınması" },
      {
        p:
          "Dilediğiniz zaman her iletideki \"aboneliği iptal et\" bağlantısını kullanarak veya info@enasuspro.com adresine e-posta göndererek onayınızı geri alabilirsiniz.",
      },
      { h: "Onay" },
      {
        p:
          "Bu kutuyu işaretleyerek reklam ve duyurulardan haberdar olmak için ticari elektronik ileti almayı kabul edersiniz. İşaretlemezseniz sadece talebinizle ilgili işlemsel iletişim yapılır.",
      },
    ],
    en: [
      { h: "Purpose" },
      {
        p:
          "Under Turkish Law No. 6563 on the Regulation of Electronic Commerce (\"ETK\") and the related Commercial Communication Regulation, your explicit consent is required in order to send you commercial electronic messages such as product announcements, campaigns, feature updates and sustainability content.",
      },
      { h: "Scope" },
      {
        p:
          "If you consent, we may contact you occasionally via email and/or SMS. This consent is for marketing only and is not required for you to use the service.",
      },
      { h: "Withdrawal" },
      {
        p:
          "You may withdraw your consent at any time by using the \"unsubscribe\" link in any message or by emailing info@enasuspro.com.",
      },
      { h: "Consent" },
      {
        p:
          "By ticking this box you consent to receiving commercial electronic messages about news and promotions. If unchecked, we will only send transactional communication related to your request.",
      },
    ],
  },
};
